import type { TruthComputeJobRecord, TruthComputeScope } from '../types/profitabilityTruth';

function eachDate(startDate: string, endDate: string): string[] {
  const out: string[] = [];
  for (let d = new Date(`${startDate}T00:00:00Z`); d.toISOString().slice(0, 10) <= endDate; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

/**
 * The "already computed" warning for a window. A multi-day Compute now runs
 * as one job per day, so no single job matches the window exactly: the window
 * counts as computed when every day in it is inside a completed job of the
 * same scope (a day job, or an older multi-day job). Returns a record spanning
 * the window (the latest covering job's details) for the warning text, or
 * null when any day is not computed yet.
 */
export function completedJobCoveringWindow(
  jobs: readonly TruthComputeJobRecord[],
  startDate: string,
  endDate: string,
  scope: TruthComputeScope,
): TruthComputeJobRecord | null {
  const done = jobs.filter((j) => j.status === 'completed' && (j.scope ?? 'all') === scope);
  const days = eachDate(startDate, endDate);
  if (days.length === 0) return null;
  const covering: TruthComputeJobRecord[] = [];
  for (const day of days) {
    const job = done.find((j) => j.startDate <= day && day <= j.endDate);
    if (!job) return null;
    covering.push(job);
  }
  const latest = [...covering].sort((a, b) =>
    String(b.completedAt ?? b.createdAt).localeCompare(String(a.completedAt ?? a.createdAt)))[0]!;
  return { ...latest, startDate, endDate };
}
